import { ModelBase } from '../abstract/model-base.model';
import { User } from '../user/user.model';

/** Nature de l'écriture de l'agent qui a posé le marqueur. */
export type AgentMarkerNature = 'creation' | 'modification';

/**
 * Marqueur « écrit par l'assistant » d'un objet métier (#1217) : l'objet a été écrit par
 * l'agent IA et n'a pas encore été revu par un humain. Servi sous `agentMarker` sur tous
 * les objets métier — d'office en lecture unitaire, par `$expand=agentMarker` en liste.
 *
 * Il disparaît au premier geste humain sur l'objet : un objet affiché avec son marqueur
 * peut l'avoir perdu entre-temps (cf. `AgentMarkerService`).
 */
export class AgentMarker extends ModelBase {

    static override CLASS_NAME: string = 'AgentMarker';

    private _actionId!: number;
    private _canKeep!: boolean;
    private _canUndo!: boolean;
    private _nature!: AgentMarkerNature;
    private _natureLabel!: string;
    private _sessionId!: string | null;
    private _user!: User | null;
    // creationDate est hérité de ModelBase

    /** Action de l'agent qui a posé le marqueur. */
    public get actionId(): number {
        return this._actionId;
    }

    public set actionId(value: any) {
        this._setValue('_actionId', value, 'integer');
    }

    /** Le serveur accepterait « Conserver » pour l'utilisateur courant. */
    public get canKeep(): boolean {
        return this._canKeep;
    }

    public set canKeep(value: any) {
        this._setValue('_canKeep', value, 'boolean');
    }

    /** Le serveur accepterait « Annuler » pour l'utilisateur courant (refus métier connu au clic seulement). */
    public get canUndo(): boolean {
        return this._canUndo;
    }

    public set canUndo(value: any) {
        this._setValue('_canUndo', value, 'boolean');
    }

    public get nature(): AgentMarkerNature {
        return this._nature;
    }

    public set nature(value: any) {
        // Le back sérialise la nature en objet { code, label } (ChoiceField).
        const code = value && typeof value === 'object' ? value.code : value;
        const label = value && typeof value === 'object' ? value.label : value;
        this._setValue('_nature', code, 'string');
        this._setValue('_natureLabel', label, 'string');
    }

    public get natureLabel(): string {
        return this._natureLabel;
    }

    /** Conversation où l'agent a écrit ; `null` pour un appel MCP. */
    public get sessionId(): string | null {
        return this._sessionId;
    }

    public set sessionId(value: any) {
        this._setValue('_sessionId', value, 'string');
    }

    /** Utilisateur au nom duquel l'agent a écrit. */
    public get user(): User | null {
        return this._user;
    }

    public set user(value: any) {
        this._setValue('_user', value, 'User');
    }
}
